import { FetchClipResponse } from "../../../../services/types";
import { Utterance, UtteranceSegment } from "./types";

type ArrayElement<ArrayType extends readonly unknown[]> =
    ArrayType extends readonly (infer ElementType)[] ? ElementType : never;

type MessageWithAllChunks = ArrayElement<FetchClipResponse['messages']> & {
    chunks: { from: number; to: number; id: string; itemId: string }[];
};

type Point = {
    position: number;
    chunkIds: string[];
    itemIds: string[];
};

const isChunkBetweenInterval = (position: number, chunk: { from: number; to: number }) => {
    return position >= chunk.from && position <= chunk.to;
};

export const createUtterances = (fetchClipResponse: FetchClipResponse): Utterance[] => {
    const mapped = new Map<string, MessageWithAllChunks>();

    /**
     * We add the message to the map.
     * The first chunk is the whole text.
     */
    fetchClipResponse.messages.forEach(message => {
        mapped.set(message.id, {
            ...message,
            chunks: [
                {
                    from: 0,
                    to: message.text.length,
                    id: message.id,
                    itemId: message.id,
                },
            ],
        });
    });

    const messagesMapped = Array.from(mapped.values());
    return messagesMapped.map(msg => {
        const segments = createSegments(msg);
        return {
            id: msg.id,
            speaker: msg.speaker,
            startTime: msg.startTime,
            endTime: msg.endTime,
            duration: msg.duration,
            content: msg.text,
            segments,
        };
    }).sort((a, b) => a.startTime - b.startTime);
};

const createSegments = (msg: MessageWithAllChunks): UtteranceSegment[] => {
    const positionPoints: Point[] = [];
    const points = new Set<number>();

    /**
     * For each chunk, we add a point in the rect.
     */
    msg.chunks.forEach(ch => {
        points.add(ch.from);
        points.add(ch.to);
    });

    const pointsArray = Array.from(points.values());

    /**
     * For each point, we check which chunks are involved in it.
     * We store their ids and itemIds.
     */
    pointsArray.forEach(p => {
        const matchingChunks = msg.chunks.filter(chunk => isChunkBetweenInterval(p, chunk));
        positionPoints.push({
            position: p,
            chunkIds: matchingChunks.map(mc => mc.id),
            itemIds: matchingChunks.map(mc => mc.itemId),
        });
    });

    const sortedPoints = positionPoints.sort((a, b) => a.position - b.position);

    const generatedChunks: any[] = [];

    for (let i = 0; i < sortedPoints.length - 1; i++) {
        const curPoint = sortedPoints[i];
        const nextPoint = sortedPoints[i + 1];
        /**
         * If the current point and nextpoint share Ids, then those ids
         * are involved in that segment.
         */
        const matchingChunkIds = curPoint.chunkIds.filter(cc =>
            Boolean(nextPoint.chunkIds.find(p => p === cc)),
        );
        const matchingItemIds = curPoint.itemIds.filter(cc =>
            Boolean(nextPoint.itemIds.find(p => p === cc)),
        );

        generatedChunks.push({
            from: curPoint.position,
            to: nextPoint.position,
            itemIds: matchingItemIds,
            chunkIds: matchingChunkIds,
        });
    }

    return generatedChunks;
};